import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import type {
  TextImportExtractRequest,
  TextImportExtractResponse,
  TextImportCreateDeckRequest,
  TextImportCreateDeckResponse,
  TextImportStatusResponse,
  DismissWordsRequest,
  DismissWordsResponse,
} from '../types'

export function useExtractWords() {
  return useMutation<TextImportExtractResponse, Error, TextImportExtractRequest>({
    mutationFn: (payload) =>
      api.post<TextImportExtractResponse>('/api/v1/text-import/extract/', payload).then((r) => r.data),
  })
}

export function useUploadFile() {
  return useMutation<TextImportExtractResponse, Error, FormData>({
    mutationFn: (form) =>
      api
        .post<TextImportExtractResponse>('/api/v1/text-import/upload/', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data),
  })
}

export function useDismissWords() {
  return useMutation<DismissWordsResponse, Error, DismissWordsRequest>({
    mutationFn: (payload) =>
      api
        .post<DismissWordsResponse>('/api/v1/words/dismiss/', payload)
        .then((r) => r.data),
  })
}

export function useCreateDeckFromText() {
  const qc = useQueryClient()
  return useMutation<TextImportCreateDeckResponse, Error, TextImportCreateDeckRequest>({
    mutationFn: (payload) =>
      api
        .post<TextImportCreateDeckResponse>('/api/v1/text-import/create-deck/', payload)
        .then((r) => r.data),
    onSuccess: () => {
      // A new deck: every deck list (Mine, the sidebar) must show it.
      qc.invalidateQueries({ queryKey: ['decks'] })
      qc.invalidateQueries({ queryKey: ['queue-composition'] })
    },
  })
}

const TERMINAL_STATUSES = new Set(['done', 'failed'])

/** Polls GET /api/v1/text-import/status/{id}/ every 2.5s until done or failed. */
export function useTextImportStatus(textImportId: string | undefined) {
  return useQuery<TextImportStatusResponse>({
    queryKey: ['text-import-status', textImportId],
    queryFn: () =>
      api
        .get<TextImportStatusResponse>(`/api/v1/text-import/status/${textImportId}/`)
        .then((r) => r.data),
    enabled: !!textImportId,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      // Stop polling once we reach a terminal state
      return status && TERMINAL_STATUSES.has(status) ? false : 2500
    },
    // Keep stale data visible while re-fetching so the counter doesn't flicker
    staleTime: 0,
  })
}
